import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import dayjs from 'dayjs';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Typography from '@mui/material/Typography';
import { UseStores } from '../../hooks/UseStores';
import { Headline } from './Title';
import CustomSummaryTextField from '../../styles/CustomSummaryTextField';
import palette from '../../styles/palette';
import { Button, Link } from '@mui/material';
import { Modal } from '@mui/material';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const DATE_FORMAT = 'MM/DD/YYYY';
dayjs.extend(isBetweenPlugin);

const CampaignDetailsCard = () => {
  const { audience, mapstore, store } = UseStores();
  const [selectedDates, setSelectedDates] = useState('');
  const [openZipModal, setOpenZipModal] = useState(false);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [zipCountModal, setZipCountModal] = useState(isTablet ? 6 : 9);

  const openModal = () => {
    setOpenZipModal(true);
  };
  const closeModal = () => {
    setOpenZipModal(false);
  };

  useEffect(() => {
    setZipCountModal(isTablet ? 6 : 9);
  }, [isTablet]);

  useEffect(() => {
    if (store.selectedAvailability) {
      const week = `${dayjs(store.selectedAvailability.date_start).format(
        DATE_FORMAT,
      )} to ${dayjs(store.selectedAvailability.date_end).format(DATE_FORMAT)}`;
      setSelectedDates(week);
    }
  }, [store.selectedAvailability]);

  const styles = {
    campaignDetailsCard: {
      width: { xs: '100%' },
      backgroundColor: palette.gt_teal_1,
      px: { xs: 'calc(3vh)', sm: 'calc(2vh)', lg: '32px' },
      py: 'calc(3vh)',
      borderRadius: '4px',
      border: `1px solid ${palette.gt_teal_3}`,
    },
    label: {
      my: { xs: '1.25vh', sm: '0.75vh' },
      typography: { xs: 'body2', sm: 'body1' },
    },
    textField: {
      width: { xs: '100%', md: '80%' },
    },
    textFieldGrid: {
      alignItem: 'flex-end',
      justifyContent: 'flex-end',
      display: 'flex',
    },
    container: {
      marginTop: 'calc(5vh)',
      marginBottom: 'calc(2vh)',
    },
    titleGrid: {
      my: 'calc(0.5vh)',
    },
    fieldGrid: {
      display: 'flex',
    },
    divider: {
      marginTop: '5%',
      marginBottom: '2%',
      color: palette.dark_gray,
    },
    linkStyle: {
      color: palette.gt_teal_6,
    },
    style: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      border: '2px solid #000',
      boxShadow: 24,
      px: 4,
      pt: 4,
    },
    boxStyle: {
      marginTop: '1rem',
      display: 'flex',
      justifyContent: 'flex-end',
    },
    closeButtonStyle: {
      color: palette.gt_text_primary,
      fontSize: '1rem',
      fontWeight: 'bold',
      padding: '12px 16px',
      borderRadius: '0',
      backgroundColor: palette.gt_green_6,
      textTransform: 'none',
    },
    zipModalTitle: {
      color: palette.gt_text_primary,
      fontSize: '1rem',
      fontWeight: 'bold',
      marginBottom: 2,
      marginTop: 2,
    },
    text: {
      marginTop: '-12px',
    },
  };

  return (
    <Card sx={styles.campaignDetailsCard} data-testid="campaign-details-test">
      <Grid
        container
        rowSpacing={{ xs: 3, sm: 3, md: 3, lg: 4 }}
        sx={styles.container}
      >
        <Grid item xs={12} sx={styles.titleGrid}>
          <Headline title="Campaign details" />
          <Divider orientation="horizontal" sx={styles.divider} />
        </Grid>
        <Grid item xs={12}>
          <Typography>
            <strong>Audience category:</strong>
          </Typography>
          <Typography data-testid="selectedAudience">
            {audience.selectedAudience.value == '' ? (
              <strong>___</strong>
            ) : (
              audience.selectedAudience.value
            )}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography sx={styles.text}>
            <strong>Selected campaign dates:</strong>
          </Typography>
          <Typography data-testid="selectedDates">
            {selectedDates == '' ? <strong>___</strong> : selectedDates}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography sx={styles.text}>
            <strong>Selected zip codes:</strong>
          </Typography>
          <Typography>
            {Object.keys(mapstore.selectedZipCodeList).length == 0 ? (
              <strong>___</strong>
            ) : (
              ''
            )}
            {Object.keys(mapstore.selectedZipCodeList).map((zip, idx) => {
              if (idx < zipCountModal) {
                if (
                  idx ==
                  Object.keys(mapstore.selectedZipCodeList).length - 1
                ) {
                  return `${zip} `;
                }
                return `${zip}, `;
              }
            })}
            {Object.keys(mapstore.selectedZipCodeList).length >
              zipCountModal && (
              <Link
                sx={styles.linkStyle}
                onClick={openModal}
                data-testid="link"
              >
                +
                {Object.keys(mapstore.selectedZipCodeList).length -
                  zipCountModal}{' '}
                more
              </Link>
            )}
            <Modal
              open={openZipModal}
              onClose={closeModal}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box sx={styles.style}>
                <Typography
                  id="modal-modal-title"
                  variant="h6"
                  component="h2"
                  sx={styles.zipModalTitle}
                >
                  Selected Zip Codes
                </Typography>
                <Typography id="modal-modal-description">
                  {Object.keys(mapstore.selectedZipCodeList).map((zip, idx) => {
                    if (
                      idx ==
                      Object.keys(mapstore.selectedZipCodeList).length - 1
                    ) {
                      return `${zip} `;
                    }
                    return `${zip}, `;
                  })}
                </Typography>
                <Box sx={styles.boxStyle} data-testid="modal">
                  <Button
                    onClick={closeModal}
                    style={styles.closeButtonStyle}
                    data-testid="close-button"
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            </Modal>
          </Typography>
        </Grid>
        <Grid item xs={12} sx={styles.fieldGrid}>
          <Grid item xs={6} lg={5}>
            <Typography gutterBottom sx={styles.label}>
              Cost per unit
            </Typography>
          </Grid>
          <Grid item xs={6} md={7} sx={styles.textFieldGrid}>
            <CustomSummaryTextField
              data-testid="rate"
              size="small"
              value={parseFloat(mapstore.rate)}
              sx={styles.textField}
              InputProps={{
                startAdornment: <AttachMoneyIcon />,
                readOnly: true,
              }}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} sx={styles.fieldGrid}>
          <Grid item xs={6} md={5}>
            <Typography gutterBottom sx={styles.label}>
              Price
            </Typography>
          </Grid>
          <Grid item xs={6} md={7} sx={styles.textFieldGrid}>
            <CustomSummaryTextField
              variant="outlined"
              size="small"
              data-testid="price"
              value={parseFloat(mapstore.price)}
              sx={styles.textField}
              InputProps={{
                startAdornment: <AttachMoneyIcon />,
                readOnly: true,
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};
export default observer(CampaignDetailsCard);
